/**
 * Arena types for continuous-pairing arena tournaments
 * Separate from existing Tournament types
 */

import type { TimeControlPreset, TimeControlKey } from './time-control.js';
import type { GameVariant } from './enums.js';
import type { BallotCeiling, OpeningMode } from './game-mode.js';

/**
 * Arena status
 */
export type ArenaStatus = 'scheduled' | 'active' | 'finished' | 'cancelled';

/**
 * Arena participant status
 */
export type ArenaParticipantStatus = 'active' | 'paused' | 'withdrawn';

/**
 * Da dove nasce una pausa d'arena.
 *
 * Viaggia nel payload di `arena:paused` perche' il client deve poter dire
 * all'utente PERCHE' e' fermo: una pausa che non ha chiesto — perche' l'app era
 * in secondo piano, o perche' la partita e' finita mentre non guardava — va
 * spiegata, mentre quella del bottone si spiega da se'.
 *
 * Campo **opzionale** nel payload: un client che non lo conosce lo ignora, e un
 * server che non lo manda non deve far leggere «ti ho messo in pausa io» a chi
 * ha premuto Pausa.
 *
 * `'altra-arena'` e' la pausa di chi e' iscritto a due arene insieme e ne ha
 * gia' una viva addosso: alla partenza della seconda non si entra in coda in
 * tutte e due — sarebbero due orologi che scorrono — e questa e' l'etichetta
 * che lo dice. Non e' `'fine-partita'`: nessuna partita e' finita, e un motivo
 * scritto male e' un'indagine sbagliata il giorno che si leggono i log.
 *
 * `'non-collegato'` e' la pausa dell'iscritto che alla partenza dell'arena non
 * ha nessun socket vivo: si e' iscritto ore prima e poi ha spento il telefono.
 * Prima di questa etichetta quell'iscritto veniva saltato e basta — nessun
 * giocatore in memoria, la riga a database ferma su `active`, e in classifica
 * una riga indistinguibile da quella di chi e' collegato e in coda: non
 * accoppiato mai, e senza niente da premere per rimediare.
 *
 * Non e' `'assenza'`, che dice «l'app era in secondo piano»: lì un socket c'e'
 * ancora e il giocatore ha dichiarato lui di non guardare. Qui non c'e' nessuna
 * connessione, e per la stessa ragione non e' nemmeno `'fine-partita'` o
 * `'altra-arena'`: di chi non e' collegato, «sta giocando» e «e' in gara
 * altrove» sono letture per definizione stantie.
 */
export type OriginePausaArena = 'bottone' | 'assenza' | 'fine-partita' | 'altra-arena' | 'non-collegato';

/**
 * Arena entity (matches arenas DB table)
 */
export interface Arena {
  id: string;
  name: string;

  // Game settings
  timeControl: TimeControlPreset;
  variant: GameVariant;

  // Arena timing
  durationMinutes: number;
  status: ArenaStatus;
  startsAt: Date | string;
  endsAt: Date | string | null;

  // Creator
  createdBy: string | null;
  createdAt: Date | string;

  // Restrictions
  minRating: number;
  maxRating: number;

  // Settings
  isRated: boolean;
  /**
   * Con che apertura comincia ogni partita dell'arena.
   *
   * Colonna `arenas.opening_mode`, NOT NULL DEFAULT 'free' dalla migration
   * 101: obbligatorio qui perche' questo tipo rispecchia la RIGA, non cio' che
   * arriva dal filo (quello e' `ArenaListItem` lato web, facoltativo apposta).
   *
   * Sostituisce la coppia `firstMoveLottery` + `ballotCeiling`, che portava un
   * avvertimento che adesso non serve piu': il flag a vero NON garantiva
   * un'apertura, perche' la tabella federale esiste solo per italiana e
   * inglese e altrove non si pescava niente. Un `ballot*` su una variante
   * senza tabella adesso ricade sulle semi-mosse casuali, quindi un'apertura
   * sorteggiata e' sorteggiata davvero — e `isDrawnOpening(openingMode)` basta
   * a saperlo, senza rifare nessuna condizione.
   */
  openingMode: OpeningMode;
  /**
   * Ogni accoppiamento si gioca andata E ritorno: due partite consecutive fra
   * gli stessi due giocatori, colori invertiti, e — se si pesca — la STESSA
   * apertura ripassata nella seconda.
   *
   * Non e' un tetto di riaccoppiamento: chiuso il ritorno i due tornano in
   * coda e possono reincontrarsi come sempre.
   *
   * Colonna `arenas.two_leg`, NOT NULL DEFAULT FALSE dalla migration 092.
   * Obbligatorio qui per la stessa ragione di `firstMoveLottery`: questo tipo
   * rispecchia la RIGA, non cio' che arriva dal filo.
   *
   * ⚠️ Le due manche sono valutate INDIPENDENTEMENTE l'una dall'altra, non
   * «o entrambe o nessuna» come nelle sfide dirette a due manche
   * (`requiresTwoLegsForRating`). Il ritorno compensa il risultato, non il
   * rating della singola partita.
   */
  twoLeg: boolean;

  // Stats (denormalized)
  playerCount: number;
  gamesCount: number;
}

/**
 * Arena scoring constants
 */
export const ARENA_SCORING = {
  WIN: 2,
  DRAW: 1,
  LOSS: 0,
  /** Il boost raddoppia la sola vittoria: 2 diventa 4. */
  BOOST_MULTIPLIER: 2,
} as const;

/**
 * Quanti millisecondi il boost toglie al TEMPO BASE (l'incremento se lo
 * prende sempre, e non passa di qui).
 *
 * Metà, su QUALUNQUE cadenza: il prezzo del boost non ha più casi speciali.
 * Prima una soglia — «il base si dimezza solo se vale almeno quaranta
 * incrementi», la stessa del berserk di Lichess — teneva indenne il tempo base
 * delle cadenze dove l'incremento È il budget della partita. Quella soglia non
 * c'è più per scelta: il boost chiede metà dell'orologio e tutto l'incremento,
 * e lo chiede allo stesso modo a ogni cadenza.
 *
 * Sulle due arene in calendario (le fasce `kind='arena'` di `schedule_slots`):
 *  - `blitz-2+5` → penalità 60 000: 2:00 diventa 1:00 e il +5 sparisce
 *    (budget su 40 mosse: 320 s → 60 s, −81,3 %);
 *  - `rapid-7+5` → penalità 210 000: 7:00 diventa 3:30 e il +5 sparisce
 *    (budget su 40 mosse: 620 s → 210 s, −66,1 %).
 *
 * Fra i diciannove preset canonici `blitz-2+5` è l'unico il cui prezzo cambia
 * con la fine della soglia: tutti gli altri avevano già il base abbastanza
 * grande (o nessun incremento da togliere). Il peso vero sta sulla griglia
 * aperta `CHALLENGE_MINUTES × CHALLENGE_INCREMENTS` con cui si crea un'arena,
 * dove ogni combinazione a base corto e incremento alto (`rapid-1+20`,
 * `classical-3+45`) prima pagava solo l'incremento.
 *
 * Nessun pavimento qui: `initialTimeMs` arriva da una cadenza risolta, e la
 * griglia parte da un minuto — la penalità minima è mezzo minuto. Chi la
 * applica all'orologio vivo (`GameRoom.applyBoost`) ha un pavimento suo.
 */
export function boostBasePenaltyMs(initialTimeMs: number): number {
  return Math.floor(initialTimeMs / 2);
}

/**
 * Una fascia di calendario per le arene: la forma in cui una riga di
 * `schedule_slots` arriva allo scheduler.
 *
 * Il nome dice ancora «template» perche' e' quello che e': il modello da cui
 * ogni giorno nasce un'arena. Quello che e' cambiato e' da dove viene — non
 * piu' una costante di questo file, ma una riga di tabella che si modifica in
 * SSH senza spedire codice (migration 082). Il tipo resta qui, in `shared`,
 * perche' e' un contratto e non un dettaglio del server.
 */
export interface ArenaTemplate {
  name: string;
  /*
   * `TimeControlKey`, non `TimeControlPreset`: la tabella dei preset è chiusa,
   * mentre la chiave è un tipo template (`categoria-minuti+incremento`) che
   * `resolveTimeControl` sa interpretare da sola. È così che l'arena può girare
   * su una cadenza fuori tabella senza spedire codice — quello che serve, da
   * quando la fascia si modifica con un UPDATE in SSH. (Le due cadenze oggi in
   * calendario, 2+5 e 7+5, un nome nella tabella l'hanno preso: le offrono
   * anche i bottoni rapidi del lobby, e quella riga vuole cadenze censite. Il
   * tipo resta aperto perché la prossima fascia potrebbe non averlo.)
   *
   * Il prefisso non è un'etichetta libera: decide la colonna di rating. 7+5
   * vale ~10 minuti sui 40 tratti convenzionali, e `categoryForClock` lo mette
   * in `rapid` — scriverlo `blitz-7+5` manderebbe i punteggi nel pool
   * sbagliato. Ora che questo valore si digita in un `UPDATE` invece di
   * passare da una code review, l'unico controllo che se ne accorge è
   * `parseTimeControlKey`; il CHECK della migration prende la forma, non la
   * contraddizione fra prefisso e orologio.
   */
  timeControl: TimeControlKey;
  variant: string;
  durationMinutes: number;
  hour: number;   // CET hour (0-23)
  minute: number;  // CET minute (0-59)
  /**
   * Giorni della settimana in cui la fascia esce, `0` = domenica.
   *
   * La numerazione è quella di `Date.getUTCDay()` e di `EXTRACT(DOW)` di
   * Postgres, e non è una coincidenza comoda: `getTodayInCet` restituisce
   * mezzanotte UTC del giorno di calendario ROMANO, quindi `getUTCDay()` su
   * quel valore È il giorno della settimana romano, senza un secondo
   * ragionamento di fuso. Vedi il commento in testa a `cetToUtc`.
   */
  daysOfWeek: readonly number[];
  /** Fascia spenta: resta in tabella, non produce arene. */
  enabled: boolean;
  /** Se sorteggiare l'apertura dalla tabella federale a inizio partita. */
  firstMoveLottery: boolean;
  /**
   * Tetto di squilibrio del sorteggio, cioè la TABELLA federale: 2 = tabella B.
   *
   * Assente vuol dire «quello di sempre», e chi pesca ci mette il suo difetto.
   * Non è una richiesta di pescare: quella è `firstMoveLottery`, e su una
   * variante senza tabella federale non si pesca comunque (`variantHasBallot`).
   */
  ballotCeiling?: BallotCeiling;
  /**
   * Se ogni accoppiamento dell'arena si gioca andata e ritorno.
   *
   * Assente = solo andata, il comportamento di sempre. Il CHECK della migration
   * 092 lo vieta alle fasce Swiss, dove un «ritorno» dentro un turno non vuol
   * dire niente.
   */
  twoLeg?: boolean;
}

/**
 * Scheduler configuration
 */
export interface ArenaSchedulerConfig {
  /*
   * Il fuso in cui si leggono l'ora e il giorno della settimana di una fascia.
   * Sta in codice e non in tabella: una colonna per riga sarebbe una seconda
   * grafia dello stesso fatto, e le fasce che vivessero in fusi diversi non
   * sono un problema che questa piattaforma ha.
   */
  timezone: string;
}

/*
 * `enabled` non c'e' piu': era un interruttore globale che non leggeva
 * nessuno, e da quando ogni fascia ha il suo (`ArenaTemplate.enabled`, colonna
 * `schedule_slots.enabled`) tenerne uno morto qui accanto sarebbe solo un modo
 * per spegnere il calendario credendo di averlo fatto.
 */
export const DEFAULT_SCHEDULER_CONFIG: ArenaSchedulerConfig = {
  timezone: 'Europe/Rome',
};

/**
 * Arena game link (matches arena_games DB table)
 */
export interface ArenaGame {
  gameId: string;
  arenaId: string;
  whiteId: string;
  blackId: string;
  result: 'white' | 'black' | 'draw' | null;
  pointsWhite: number;
  pointsBlack: number;
  whiteBoost: boolean;
  blackBoost: boolean;
  movesCount: number;
  createdAt: Date | string;
}

/**
 * Calculate points for an arena game result
 */
export function calculateArenaPoints(
  result: 'win' | 'draw' | 'loss',
  boosted: boolean
): number {
  let points = 0;

  switch (result) {
    case 'win':
      points = ARENA_SCORING.WIN;
      if (boosted) {
        points *= ARENA_SCORING.BOOST_MULTIPLIER;
      }
      break;
    case 'draw':
      points = ARENA_SCORING.DRAW;
      break;
    case 'loss':
      points = ARENA_SCORING.LOSS;
      break;
  }

  return points;
}
